import { graphql } from 'gatsby';
import React from 'react';
import { css } from '@emotion/react';
import ArticleCard from '../../resources/article-card';
import Container from '../../primitives/grid/container';
import paddingDefault from '../../utils/padding-default';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import { breakpoints } from '../../primitives/tokens';
import { ArticleResult } from '../../resources/types';

const styles = {
    blogrollHeading: css({
        marginBottom: '24px',
        fontSize: '32px',
        lineHeight: 1.25,
        textAlign: 'center',
        [breakpoints.mb]: {
            marginBottom: '16px',
        },
    }),
    blogrollSubheading: css({
        marginBottom: '32px',
        maxWidth: '768px',
        fontSize: '20px',
        lineHeight: '28px',
        textAlign: 'center',
        whiteSpace: 'pre-line',
        marginRight: 'auto',
        marginLeft: 'auto',
        [breakpoints.mb]: {
            fontSize: '18px',
            marginBottom: '16px',
        },
    }),
    blogrollGrid: {
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        rowGap: '64px',
        display: 'grid',
        gap: '32px',
        [breakpoints.tb]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [breakpoints.mb]: {
            gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
        },
    },
    blogrollGridItem: css({
        flex: '1 1 auto',
        width: '100%',
    }),
};

const Blogroll: React.FC<Queries.BlogrollDataFragment> = (data) => {
    const { heading, subheading, relatedContent } = data;

    if (!relatedContent || relatedContent.length === 0) {
        return null;
    }

    return (
        <section css={paddingDefault()}>
            <Container>
                {relatedContent && relatedContent.length > 0 && (
                    <h2 css={styles.blogrollHeading}>
                        {heading ? apCaseOnlyTitleTags(heading) : 'Recommended for You'}
                    </h2>
                )}
                <div
                    css={[RichTextEditorStyles, styles.blogrollSubheading]}
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: subheading || '' }}
                />
                <ul css={styles.blogrollGrid}>
                    {!!relatedContent && relatedContent.map((item) => {
                        if (!item) return null;

                        const { title, url } = item;

                        if ('content' in item) {
                            return (
                                <ArticleCard key={item.url} article={item as ArticleResult} />
                            );
                        }

                        if ('sections' in item) {
                            const { sections } = item;
                            // eslint-disable-next-line no-underscore-dangle
                            const hero = sections?.find(x => x?.__typename === 'CsHero');

                            let metaTitle = '';
                            let metaDescription = '';
                            let metaImage: Partial<ContentStackImage>|null = null;

                            if ('seo' in item) {
                                if (item.seo?.metaTitle) {
                                    metaTitle = item.seo.metaTitle;
                                }
                                if (item.seo?.metaDescription) {
                                    metaDescription = item.seo.metaDescription;
                                }
                                if (item.seo?.metaImage) {
                                    metaImage = item.seo.metaImage;
                                }
                            }

                            const cardTitle = hero?.heading || metaTitle || title;
                            const cardContent = hero?.copy || metaDescription || '';
                            const cardImage = hero?.image ?? metaImage ?? undefined;

                            return (
                                <li css={styles.blogrollGridItem} key={item.title}>
                                    <ArticleCard
                                        key={item.url}
                                        article={{
                                            title: cardTitle || '',
                                            featuredImage: cardImage as ContentStackImage | undefined,
                                            content: cardContent || '',
                                            url: url || '',
                                        }}
                                    />
                                </li>
                            );
                        }

                        return null;
                    })}
                </ul>
            </Container>
        </section>
    );
};

export const query = graphql`
    fragment BlogrollData on CsBlogroll {
        ... on CsBlogroll {
            heading
            subheading
            relatedContent {
                __typename
                ...BlogrollArticleFrag
                ...BlogrollPressFrag
                ...BlogrollServiceFrag
                ...BlogrollPageFrag
            }
        }
    }
    fragment BlogrollArticleFrag on CsArticle {
        title
        content
        url
        featuredImage {
            filename
            title
            description
            url
            contentStackAsset(width: 1440)
        }
    }

    fragment BlogrollPressFrag on CsPressRelease {
        title
        content
        url
        featuredImage {
            filename
            title
            description
            url
            contentStackAsset(width: 1440)
        }
    }

    fragment BlogrollServiceFrag on CsServiceDetail {
        title
        url
        sections {
            __typename
            ... on CsHero {
                heading
                copy
                image {
                    filename
                    title
                    description
                    url
                    contentStackAsset(width: 1440)
                }
            }
        }
        seo {
            metaTitle
            metaDescription
            metaImage {
                filename
                title
                url
                contentStackAsset(width: 1440)
            }
        }
    }

    fragment BlogrollPageFrag on CsPageBuilder {
        title
        url
        sections {
            __typename
            ... on CsHero {
                heading
                copy
                image {
                    filename
                    title
                    url
                    contentStackAsset(width: 1440)
                }
            }
        }
        seo {
            metaTitle
            metaDescription
            metaImage {
                filename
                title
                url
                contentStackAsset(width: 1440)
            }
        }
    }
`;

export default Blogroll;
